import React, { useState } from 'react';
import { X, Building2, Link2, Unlink, CheckCircle2, ChevronRight, Activity, Globe } from 'lucide-react';
import { HospitalGroup, Hospital } from '../types';
import * as storageService from '../services/storageService';

interface GroupLinkModalProps {
    isOpen: boolean;
    onClose: () => void;
    group: HospitalGroup | null;
    allHospitals: Hospital[];
    onUpdate: () => void;
}

const GroupLinkModal: React.FC<GroupLinkModalProps> = ({ isOpen, onClose, group, allHospitals, onUpdate }) => {
    const [isUpdating, setIsUpdating] = useState<string | null>(null);

    if (!isOpen || !group) return null;

    const linkedHospitals = allHospitals.filter(h => h.groupId === group.id);
    const availableHospitals = allHospitals.filter(h => !h.groupId && h.isActive !== false);

    const handleLink = async (hospitalId: string) => {
        setIsUpdating(hospitalId);
        try {
            await storageService.updateHospitalGroup(hospitalId, group.id);
            onUpdate();
        } catch (e) {
            console.error("Error linking hospital", e);
            alert("Error al vincular el hospital");
        } finally {
            setIsUpdating(null);
        }
    };

    const handleUnlink = async (hospitalId: string) => {
        setIsUpdating(hospitalId);
        try {
            await storageService.updateHospitalGroup(hospitalId, null);
            onUpdate();
        } catch (e) {
            console.error("Error unlinking hospital", e);
            alert("Error al desvincular el hospital");
        } finally {
            setIsUpdating(null);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in overflow-y-auto w-full h-full">
            <div className="bg-slate-50 w-full max-w-4xl rounded-[2rem] shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
                
                {/* Header */}
                <div className="p-8 pb-6 border-b border-slate-200/60 flex justify-between items-start bg-white">
                    <div className="flex gap-4 items-center">
                        <div className="w-14 h-14 bg-indigo-50 rounded-2xl border border-indigo-100 flex items-center justify-center p-2 hidden sm:flex">
                            {group.logoUrl ? (
                                <img src={group.logoUrl} className="w-full h-full object-contain" alt="Logo" />
                            ) : (
                                <Building2 className="w-7 h-7 text-indigo-500" />
                            )}
                        </div>
                        <div>
                            <h2 className="text-2xl font-black text-slate-800 tracking-tight">Vincular Sucursales</h2>
                            <p className="text-sm font-medium text-slate-500 mt-1 flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-indigo-500"></span>
                                {group.name}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2.5 bg-slate-100 hover:bg-slate-200 text-slate-500 rounded-full transition-colors hidden sm:block">
                        <X className="w-5 h-5" />
                    </button>
                    {/* Mobile close */}
                    <button onClick={onClose} className="p-2 -mt-2 -mr-2 text-slate-400 hover:text-slate-600 sm:hidden">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 sm:p-8 flex flex-col lg:flex-row gap-8">
                    
                    {/* LEFT COL: LINKED HOSPITALS */}
                    <div className="flex-1 bg-white p-6 rounded-3xl border border-slate-200/60 shadow-sm flex flex-col h-fit">
                        <div className="mb-6 flex items-center justify-between">
                            <div>
                                <h3 className="font-bold text-slate-800 text-lg flex items-center gap-2">
                                    <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                                    Hospitales de la Cadena
                                </h3>
                                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mt-1">{linkedHospitals.length} sucursales activas</p>
                            </div>
                        </div>

                        {linkedHospitals.length === 0 ? (
                            <div className="py-12 border-2 border-dashed border-slate-100 rounded-2xl flex flex-col items-center justify-center text-center px-4">
                                <Building2 className="w-10 h-10 text-slate-300 mb-3" />
                                <p className="text-slate-500 font-bold text-sm">Sin sucursales asignadas</p>
                                <p className="text-xs text-slate-400 mt-1 max-w-[200px]">Los hospitales aparecerán aquí cuando los vincules desde el panel derecho.</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {linkedHospitals.map(h => (
                                    <div key={h.id} className="group bg-slate-50 border border-slate-200 rounded-2xl p-4 flex items-center justify-between transition-colors hover:bg-white hover:border-slate-300 hover:shadow-sm">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-white rounded-xl border border-slate-200 flex items-center justify-center p-1.5 shadow-sm">
                                                {h.branding?.logoUrl ? <img src={h.branding.logoUrl} className="w-full h-full object-contain" /> : <Building2 className="w-5 h-5 text-slate-400" />}
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-slate-700 text-sm leading-tight">{h.name}</h4>
                                                <p className="text-[10px] text-slate-400 font-mono mt-0.5 select-all">/{h.handle}</p>
                                            </div>
                                        </div>
                                        <button 
                                            onClick={() => handleUnlink(h.id)}
                                            disabled={isUpdating === h.id}
                                            className="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-100 text-slate-400 hover:text-red-500 hover:bg-red-50 border border-transparent hover:border-red-100 transition-all focus:outline-none disabled:opacity-50"
                                            title="Retirar de la cadena"
                                        >
                                            {isUpdating === h.id ? <div className="w-4 h-4 border-2 border-slate-300 border-t-red-500 rounded-full animate-spin" /> : <Unlink className="w-4 h-4" />}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* RIGHT COL: AVAILABLE HOSPITALS */}
                    <div className="flex-1 bg-gradient-to-br from-indigo-50/50 to-white p-6 rounded-3xl border border-indigo-100/60 shadow-sm flex flex-col h-fit">
                        <div className="mb-6 flex items-center justify-between">
                            <div>
                                <h3 className="font-bold text-indigo-900 text-lg flex items-center gap-2">
                                    <Globe className="w-5 h-5 text-indigo-500" />
                                    Hospitales Independientes
                                </h3>
                                <p className="text-[11px] font-bold text-indigo-400/80 uppercase tracking-wider mt-1">{availableHospitals.length} hospitales encontrados</p>
                            </div>
                        </div>

                        {availableHospitals.length === 0 ? (
                            <div className="py-12 border-2 border-dashed border-indigo-100 rounded-2xl flex flex-col items-center justify-center text-center px-4">
                                <Activity className="w-10 h-10 text-indigo-200 mb-3" />
                                <p className="text-indigo-400 font-bold text-sm">No hay hospitales independientes</p>
                                <p className="text-xs text-indigo-300 mt-1 max-w-[200px]">Todos los hospitales activos del sistema ya pertenecen a una cadena.</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {availableHospitals.map(h => (
                                    <div key={h.id} className="bg-white border border-indigo-100 rounded-2xl p-4 flex items-center justify-between transition-colors hover:border-indigo-300 hover:shadow-md hover:shadow-indigo-500/5 group">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-slate-50 rounded-xl border border-slate-100 flex items-center justify-center p-1.5">
                                                {h.branding?.logoUrl ? <img src={h.branding.logoUrl} className="w-full h-full object-contain grayscale opacity-70 group-hover:grayscale-0 group-hover:opacity-100 transition-all" /> : <Building2 className="w-5 h-5 text-slate-300" />}
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-indigo-950 text-sm leading-tight group-hover:text-indigo-700 transition-colors">{h.name}</h4>
                                                <p className="text-[10px] text-slate-400 font-mono mt-0.5 select-all">/{h.handle}</p>
                                            </div>
                                        </div>
                                        
                                        <button 
                                            onClick={() => handleLink(h.id)}
                                            disabled={isUpdating === h.id}
                                            className="px-4 py-2 flex items-center gap-1.5 rounded-xl bg-indigo-50 hover:bg-indigo-600 text-indigo-600 hover:text-white font-bold text-[11px] border border-indigo-100 hover:border-indigo-600 transition-all focus:outline-none uppercase tracking-wider disabled:opacity-50"
                                        >
                                            {isUpdating === h.id ? (
                                                <div className="w-3.5 h-3.5 border-2 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
                                            ) : (
                                                <>
                                                    Vincular
                                                    <Link2 className="w-3 h-3" />
                                                </>
                                            )}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                </div>

                <div className="p-6 border-t border-slate-200/60 bg-white text-center flex justify-between items-center px-4 sm:px-8">
                    <p className="text-xs font-bold text-slate-400">Los cambios se aplican de inmediato en la base de datos.</p>
                    <button onClick={onClose} className="px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-black rounded-xl transition-all shadow-lg shadow-indigo-600/30 active:scale-95 text-sm">
                        Hecho
                    </button>
                </div>
            </div>
        </div>
    );
};

export default GroupLinkModal;
